/**
 * useCharacterStore - Hook for managing player character and progress
 * 
 * Now uses PlayerBootstrapStore for initial load to reduce duplicate requests.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { usePlayerAuth } from '@/hooks/usePlayerAuth';
import { calculateLevelFromPoints } from '@/utils/playerProgression';
import { 
  loadPlayerBootstrap, 
  getCachedPlayerBootstrap,
  updateCachedCharacterProfile,
  updateCachedUserProgress,
  type CharacterProfile,
  type UserProgressState
} from '@/stores/playerBootstrapStore';
import { recordRpcCall } from '@/lib/rpcDedupDiag';
import { useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface CharacterData {
  name: string;
  skinTone: string;
  gender: string;
  hairstyle: string;
  hairColor: string;
  clothing: string;
  equipped_accessories?: string[];
  appearance?: {
    hairColor: string;
    skinTone: string;
    outfit: string;
    accessories: string[];
    pants?: string;
    shirtColor?: string;
    pantsColor?: string;
    gender?: string;
    current_vehicle?: string | null;
  };
}

interface PlayerProgress {
  currentLocation: string;
  totalPoints: number;
  money: number;
  level: number;
  completedScenarios: string[];
  unlockedLocations: string[];
}

// Default character for new users
const DEFAULT_CHARACTER: CharacterData = {
  name: 'Pelaaja',
  skinTone: '#FDBCB4',
  gender: 'male',
  hairstyle: 'short',
  hairColor: '#8B4513',
  clothing: '#FF6B35',
  equipped_accessories: [],
  appearance: {
    hairColor: '#8B4513',
    skinTone: '#FDBCB4',
    outfit: '#FF6B35',
    accessories: [],
    pants: '#4A90E2',
    shirtColor: '#FF6B35',
    pantsColor: '#4A90E2',
    gender: 'male'
  }
};

// Default progress for new users
const DEFAULT_PROGRESS: PlayerProgress = {
  currentLocation: 'home',
  totalPoints: 0,
  money: 1500,
  level: 1,
  completedScenarios: [],
  unlockedLocations: ['home', 'bank', 'supermarket', 'cafe']
};

// Fetch character data via bootstrap store
const fetchCharacterData = async (playerId: string, sessionToken?: string): Promise<CharacterData> => {
  if (!sessionToken) {
    return DEFAULT_CHARACTER;
  }

  // Try bootstrap cache first
  const cached = getCachedPlayerBootstrap(playerId);
  if (cached?.characterProfile) {
    return bootstrapToCharacterData(cached.characterProfile);
  }

  // Load via bootstrap
  const bootstrap = await loadPlayerBootstrap({
    playerId,
    sessionToken,
    reason: 'useCharacterStore:character'
  });

  if (bootstrap.characterProfile) {
    return bootstrapToCharacterData(bootstrap.characterProfile);
  }

  return DEFAULT_CHARACTER;
};

// Convert bootstrap format to CharacterData
function bootstrapToCharacterData(profile: CharacterProfile): CharacterData {
  return {
    name: profile.name,
    skinTone: profile.skinTone,
    gender: profile.gender,
    hairstyle: profile.hairstyle,
    hairColor: profile.hairColor,
    clothing: profile.clothing,
    equipped_accessories: profile.appearance?.accessories || [],
    appearance: profile.appearance as CharacterData['appearance']
  };
}

// Fetch progress data via bootstrap store
const fetchProgressData = async (playerId: string, sessionToken?: string): Promise<PlayerProgress> => {
  if (!sessionToken) {
    return DEFAULT_PROGRESS;
  }

  // Try bootstrap cache first
  const cached = getCachedPlayerBootstrap(playerId);
  if (cached?.userProgressState) {
    return cached.userProgressState;
  }

  // Load via bootstrap
  const bootstrap = await loadPlayerBootstrap({
    playerId,
    sessionToken,
    reason: 'useCharacterStore:progress'
  });

  return bootstrap.userProgressState || DEFAULT_PROGRESS;
};

export const useCharacterStore = () => {
  const { playerSession } = usePlayerAuth();
  const playerId = playerSession?.playerId;
  const sessionToken = playerSession?.sessionToken;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query for character data with React Query
  const { data: character, isLoading: characterLoading } = useQuery({
    queryKey: ['character', playerId, sessionToken],
    queryFn: () => fetchCharacterData(playerId!, sessionToken),
    enabled: !!playerId && !!sessionToken,
    staleTime: 60_000, // Use cached data for 1 minute
    gcTime: 120_000,
    retry: 1,
  });

  // Query for progress data with React Query
  const { data: progress, isLoading: progressLoading } = useQuery({
    queryKey: ['progress', playerId],
    queryFn: () => fetchProgressData(playerId!, sessionToken),
    enabled: !!playerId && !!sessionToken,
    staleTime: 60_000,
    gcTime: 120_000,
  });

  const loading = characterLoading || progressLoading;

  // Mutation for saving character
  const saveCharacterMutation = useMutation({
    mutationFn: async (characterData: CharacterData) => {
      if (!playerId || !playerSession?.sessionToken) {
        throw new Error('No player ID or session token');
      }

      const enhancedCharacterData = {
        ...characterData,
        equipped_accessories: characterData.equipped_accessories || [],
        skinTone: characterData.skinTone || '#FDBCB4',
        gender: characterData.gender || 'male',
        hairstyle: characterData.hairstyle || 'short',
        hairColor: characterData.hairColor || '#8B4513',
        clothing: characterData.clothing || '#4A90E2',
        appearance: {
          hairColor: characterData.hairColor || '#8B4513',
          skinTone: characterData.skinTone || '#FDBCB4',
          outfit: characterData.clothing || '#4A90E2',
          accessories: characterData.equipped_accessories || characterData.appearance?.accessories || [],
          pants: characterData.appearance?.pants || characterData.appearance?.pantsColor || '#4A90E2',
          shirtColor: characterData.appearance?.shirtColor || characterData.clothing || '#4A90E2',
          pantsColor: characterData.appearance?.pantsColor || characterData.appearance?.pants || '#4A90E2',
          gender: characterData.gender || characterData.appearance?.gender || 'male',
          hairstyle: characterData.hairstyle || 'short',
          ...characterData.appearance
        }
      };

      // console.log('Saving character with data:', {
      //   playerId,
      //   characterData: enhancedCharacterData
      // });

      // Use RPC function that handles session token and upsert in one transaction
      const { data, error } = await supabase.rpc('save_character_profile', {
        p_player_id: playerId,
        p_session_token: playerSession.sessionToken,
        p_name: enhancedCharacterData.name,
        p_skin_tone: enhancedCharacterData.skinTone,
        p_gender: enhancedCharacterData.gender,
        p_hairstyle: enhancedCharacterData.hairstyle,
        p_hair_color: enhancedCharacterData.hairColor,
        p_clothing: enhancedCharacterData.clothing,
        p_appearance: enhancedCharacterData.appearance
      });

      if (error) {
        // console.error('Error saving character:', error);
        throw error;
      }

      // console.log('Character saved successfully:', data);
      return enhancedCharacterData;
    },
    onSuccess: (savedData) => {
      // Update bootstrap cache
      if (playerId) {
        updateCachedCharacterProfile(playerId, {
          name: savedData.name,
          skinTone: savedData.skinTone,
          gender: savedData.gender,
          hairstyle: savedData.hairstyle,
          hairColor: savedData.hairColor,
          clothing: savedData.clothing,
          appearance: savedData.appearance
        });
      }
      queryClient.invalidateQueries({ queryKey: ['character', playerId] });
      // Success - silent per policy
    },
    onError: (error) => {
      toast({ title: "Virhe", description: "Hahmon tallentaminen epäonnistui", variant: "destructive" });
    }
  });

  // Deduplication refs for save_player_progress
  const lastSavedKeyRef = useRef<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingProgressRef = useRef<PlayerProgress | null>(null);
  // CRITICAL: In-flight promise guard - prevents concurrent mutations
  const saveInFlightRef = useRef<Promise<PlayerProgress> | null>(null);
  // Last save timestamp for cooldown
  const lastSaveAtRef = useRef<number>(0);
  // INCREASED: 1000ms cooldown to catch all answer-related updates in one batch
  const SAVE_COOLDOWN_MS = 1000;
  // Debounce time - how long to wait for additional updates before saving
  const DEBOUNCE_MS = 400;

  // Mutation for saving progress
  const saveProgressMutation = useMutation({
    mutationFn: async (progressData: PlayerProgress) => {
      if (!playerId || !playerSession) throw new Error('No player session');
      
      // Generate correlation ID for tracing
      const correlationId = crypto.randomUUID().slice(0, 8);
      
      // Generate deduplication key based on progress state
      const saveKey = `${playerId}:${progressData.totalPoints}:${progressData.level}:${progressData.money}`;
      
      // GUARD 1: Check for duplicate save (same data)
      if (lastSavedKeyRef.current === saveKey) {
        console.debug(`[PROGRESS] save_player_progress SKIPPED (duplicate) | corr=${correlationId}`, { saveKey });
        return progressData;
      }
      
      // GUARD 2: Cooldown - skip if recently saved (unless data is different)
      const now = Date.now();
      const timeSinceLastSave = now - lastSaveAtRef.current;
      if (timeSinceLastSave < SAVE_COOLDOWN_MS && lastSavedKeyRef.current) {
        console.debug(`[PROGRESS] save_player_progress SKIPPED (cooldown) | corr=${correlationId} | age=${timeSinceLastSave}ms`);
        // Store for later retry
        pendingProgressRef.current = progressData;
        return progressData;
      }

      console.log(`[PROGRESS] save_player_progress fired | corr=${correlationId}`, { 
        points: progressData.totalPoints, 
        level: progressData.level,
        money: progressData.money 
      });

      // Record for dedup diagnostics
      recordRpcCall('save_player_progress', `corr=${correlationId}`, false);

      // Use RPC function to save progress with session validation
      // This ensures session token and database operation happen in same transaction
      const { data, error } = await supabase.rpc('save_player_progress', {
        p_session_token: playerSession.sessionToken,
        p_player_id: playerId,
        p_progress_data: progressData as any
      });

      if (error) throw error;
      if (!data) throw new Error('Failed to save progress');
      
      // Mark as saved after success
      lastSavedKeyRef.current = saveKey;
      lastSaveAtRef.current = Date.now();
      console.debug(`[PROGRESS] save_player_progress SUCCESS | corr=${correlationId}`);
      
      return progressData;
    },
    onSuccess: (savedProgress) => {
      // Update bootstrap cache
      if (playerId) {
        updateCachedUserProgress(playerId, savedProgress);
      }
      queryClient.invalidateQueries({ queryKey: ['progress', playerId] });
    },
    onError: (error) => {
      console.error('[PROGRESS] save_player_progress ERROR:', error);
    }
  });

  const saveCharacter = async (characterData: CharacterData) => {
    await saveCharacterMutation.mutateAsync(characterData);
  };

  const loadCharacterFromDatabase = async () => {
    await queryClient.invalidateQueries({ queryKey: ['character', playerId] });
  };

  // Debounced updateProgress to batch rapid updates
  // CRITICAL: Uses in-flight promise to prevent concurrent mutations
  // DESIGN: All calls to addPoints, addMoney, etc. within a single answer 
  // submission are batched into a SINGLE save_player_progress call
  const updateProgress = useCallback(async (updates: Partial<PlayerProgress>) => {
    if (!progress) return;
    
    // Merge updates with any pending progress (for batching rapid updates)
    const baseProgress = pendingProgressRef.current || progress;
    const newProgress = { ...baseProgress, ...updates };
    
    // If a save is already in flight, store pending and return
    if (saveInFlightRef.current) {
      pendingProgressRef.current = newProgress;
      console.debug('[PROGRESS] updateProgress: save in-flight, queuing update');
      return;
    }
    
    // Store pending progress for debounce
    pendingProgressRef.current = newProgress;
    
    // Clear previous timeout (extends debounce window for rapid updates)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce: wait DEBOUNCE_MS before saving
    // This allows all answer-related updates (points, streak bonus, challenge progress)
    // to be batched into a single save
    saveTimeoutRef.current = setTimeout(async () => {
      const progressToSave = pendingProgressRef.current;
      if (!progressToSave) return;
      
      pendingProgressRef.current = null;
      
      // Set in-flight promise
      const savePromise = saveProgressMutation.mutateAsync(progressToSave);
      saveInFlightRef.current = savePromise;
      
      try {
        await savePromise;
      } finally {
        saveInFlightRef.current = null;
        
        // Check if new updates came in while saving
        // BUT respect cooldown - don't immediately trigger another save
        if (pendingProgressRef.current) {
          const timeSinceLastSave = Date.now() - lastSaveAtRef.current;
          if (timeSinceLastSave >= SAVE_COOLDOWN_MS) {
            console.debug('[PROGRESS] updateProgress: processing queued update after save');
            // Recursively process pending
            const pending = pendingProgressRef.current;
            pendingProgressRef.current = null;
            await saveProgressMutation.mutateAsync(pending);
          } else {
            // Schedule a delayed save after cooldown expires
            const delayMs = SAVE_COOLDOWN_MS - timeSinceLastSave;
            console.debug(`[PROGRESS] updateProgress: scheduling delayed save in ${delayMs}ms`);
            setTimeout(async () => {
              const delayed = pendingProgressRef.current;
              if (delayed && !saveInFlightRef.current) {
                pendingProgressRef.current = null;
                await saveProgressMutation.mutateAsync(delayed);
              }
            }, delayMs + 50); // Add 50ms buffer
          }
        }
      }
    }, DEBOUNCE_MS);
  }, [progress, saveProgressMutation]);

  const addPoints = (amount: number) => {
    if (!progress) return;
    const newPoints = progress.totalPoints + amount;
    const newLevel = calculateLevelFromPoints(newPoints);
    updateProgress({ 
      totalPoints: newPoints, 
      level: newLevel 
    });
    
    // console.log(`🎯 Added ${amount} points. Total: ${newPoints}, Level: ${newLevel}`);
  };

  const addMoney = async (amount: number) => {
    if (!progress) return;
    // console.log(`💰 Adding ${amount}€ to player's balance (current: ${progress.money}€)`);
    const newMoney = Math.max(0, progress.money + amount);
    await updateProgress({ money: newMoney });
    
    // console.log(`💰 Money updated: ${progress.money}€ → ${newMoney}€ (change: ${amount > 0 ? '+' : ''}${amount}€)`);
  };

  const revertMoney = async (amount: number) => {
    if (!progress) return;
    // console.log(`⏮️ Reverting ${amount}€ from player's balance`);
    const newMoney = Math.max(0, progress.money - amount);
    await updateProgress({ money: newMoney });
  };

  const revertExperience = (amount: number) => {
    if (!progress) return;
    // console.log(`⏮️ Reverting ${amount} experience points`);
    const newPoints = Math.max(0, progress.totalPoints - amount);
    const newLevel = calculateLevelFromPoints(newPoints);
    updateProgress({ 
      totalPoints: newPoints, 
      level: newLevel 
    });
  };

  const completeScenario = (scenarioId: string, pointsGain: number = 25, moneyGain: number = 0) => {
    if (!progress) return;
    if (!progress.completedScenarios.includes(scenarioId)) {
      const newPoints = progress.totalPoints + pointsGain;
      const newLevel = calculateLevelFromPoints(newPoints);
      
      updateProgress({
        completedScenarios: [...progress.completedScenarios, scenarioId],
        totalPoints: newPoints,
        level: newLevel,
        money: progress.money + moneyGain
      });
    }
  };

  const unlockLocation = (locationId: string) => {
    if (!progress) return;
    if (!progress.unlockedLocations.includes(locationId)) {
      updateProgress({
        unlockedLocations: [...progress.unlockedLocations, locationId]
      });
    }
  };

  const moveToLocation = (locationId: string) => {
    if (!progress) return;
    updateProgress({ currentLocation: locationId });
  };

  return {
    character: character || null,
    progress: progress || {
      currentLocation: 'home',
      totalPoints: 0,
      money: 1500,
      level: 1,
      completedScenarios: [],
      unlockedLocations: ['home', 'bank', 'supermarket', 'cafe']
    },
    isFirstLogin: false,
    loading,
    saveCharacter,
    updateProgress,
    addPoints,
    addMoney,
    revertMoney,
    revertExperience,
    completeScenario,
    unlockLocation,
    moveToLocation,
    loadCharacterFromDatabase,
    addExperience: addPoints
  };
};
